import React, { useState } from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import DownhillSkiing from '@mui/icons-material/DownhillSkiing';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { relativeTime, updatedChipColor, isStale } from './hillCaptainUtils';

// ── Status config ──────────────────────────────────────────────────────────

const TOBOGGAN_STATUSES = [
  'Ready', 'In Use', 'Needs Repair', 'Maintenance', 'Out of Service', 'Missing',
];

const TOBOGGAN_LOCATION_OPTIONS = [
  'Snowmobile', "Teacher's Pet", 'Quad', 'Gunpowder', 'Powder Puff',
];

const TOBOGGAN_ROW_BORDER = {
  'Ready':          '#4caf50',
  'In Use':         '#2196f3',
  'Needs Repair':   '#f44336',
  'Maintenance':    '#ff9800',
  'Out of Service': '#f44336',
  'Missing':        '#f44336',
};

const TOBOGGAN_CHIP_COLOR = {
  'Ready':          'success',
  'In Use':         'info',
  'Needs Repair':   'error',
  'Maintenance':    'warning',
  'Out of Service': 'error',
  'Missing':        'error',
};

// ── Component ──────────────────────────────────────────────────────────────

export const TobogganStatus = ({ tobogganData, onSave }) => {
  const [toboggans,       setToboggans]       = useState(tobogganData ?? []);
  const [isEditing,       setIsEditing]       = useState(false);
  const [error,           setError]           = useState(null);
  const [isSaving,        setIsSaving]        = useState(false);
  const [preEditSnapshot, setPreEditSnapshot] = useState(null);
  const [deletedIds,      setDeletedIds]      = useState([]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleRowClick = () => {
    if (!isEditing) {
      setPreEditSnapshot(toboggans);
      setIsEditing(true);
    }
  };

  const handleAdd = () => {
    if (!isEditing) setPreEditSnapshot(toboggans);
    setToboggans(prev => [
      ...prev,
      { id: `new-${Date.now()}`, type: 'toboggan', location: '', status: 'Ready' },
    ]);
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    setToboggans(prev => prev.filter(t => t.id !== id));
    if (!id.startsWith('new-')) setDeletedIds(prev => [...prev, id]);
  };

  const handleChange = (id, field, value) => {
    setToboggans(prev => prev.map(t =>
      t.id === id ? { ...t, [field]: value } : t
    ));
    setError(null);
  };

  const handleCancel = () => {
    setToboggans(preEditSnapshot ?? tobogganData ?? []);
    setPreEditSnapshot(null);
    setDeletedIds([]);
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const errors = await onSave(toboggans, deletedIds);
      const now = Date.now();
      setToboggans(current => current.map(t => ({
        ...t,
        lastUpdated: errors?.find(e => e.id === t.id) ? t.lastUpdated : now,
        status: errors?.find(e => e.id === t.id) ? 'error' : t.status,
      })));
      setDeletedIds([]);
      setIsEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to save toboggan data');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Card>
      <CardHeader
        avatar={<DownhillSkiing />}
        title={<Typography variant="h6">Toboggans</Typography>}
        action={
          <Button size="small" startIcon={<Add />} onClick={handleAdd}>
            Add
          </Button>
        }
        sx={{ pb: 1 }}
      />
      <Divider />


      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mx: 1.5, mt: 1 }}>{error}</Alert>
      )}

      {/* Toboggan list */}
      <Box>
        {toboggans.map((toboggan, index) => {
          const rowBorder = TOBOGGAN_ROW_BORDER[toboggan.status] ?? 'transparent';
          const chipColor = TOBOGGAN_CHIP_COLOR[toboggan.status] ?? 'default';
          const relTime   = relativeTime(toboggan.lastUpdated);
          const timeColor = updatedChipColor(toboggan.lastUpdated);
          const stale     = isStale(toboggan.lastUpdated);

          return (
            <Box
              key={toboggan.id}
              data-testid="toboggan-row"
              data-status={toboggan.status}
              onClick={handleRowClick}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.75, py: 1.25,
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                borderLeft: isEditing ? 'none' : `3px solid ${rowBorder}`,
                cursor: isEditing ? 'default' : 'pointer',
              }}
            >
              {/* Delete button */}
              {isEditing && (
                <IconButton
                  data-testid={`delete-btn-${index}`}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleDelete(toboggan.id); }}
                  sx={{
                    width: 20, height: 20, p: 0,
                    bgcolor: '#c62828', color: 'white',
                    '&:hover': { bgcolor: '#b71c1c' },
                    flexShrink: 0,
                  }}
                >
                  <CancelIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}

              {/* Left: location + chips */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.625 }}>

                {isEditing ? (
                  <Autocomplete
                    freeSolo
                    options={TOBOGGAN_LOCATION_OPTIONS}
                    value={toboggan.location}
                    onInputChange={(_, value) => handleChange(toboggan.id, 'location', value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Location"
                        inputProps={{ ...params.inputProps, 'data-testid': `location-input-${index}` }}
                        sx={{ '& .MuiInputBase-input': { fontSize: 12, fontWeight: 600 } }}
                      />
                    )}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {stale && (
                      <WarningAmberIcon
                        data-testid="stale-icon"
                        sx={{ fontSize: 14, color: 'error.light', flexShrink: 0 }}
                      />
                    )}
                    <Typography fontSize={13} fontWeight={600}>{toboggan.location}</Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 0.625, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip
                    label={toboggan.status}
                    color={chipColor}
                    size="small"
                    sx={{ fontSize: 9, height: 18, fontWeight: 700 }}
                  />
                  {relTime && (
                    <Chip
                      label={relTime}
                      color={timeColor}
                      size="small"
                      sx={{ fontSize: 9, height: 18, fontWeight: 700 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Right: status label / dropdown */}
              {isEditing ? (
                <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
                  <Select
                    value={toboggan.status}
                    onChange={(e) => handleChange(toboggan.id, 'status', e.target.value)}
                    SelectDisplayProps={{ 'data-testid': `status-select-${index}` }}
                    sx={{ fontSize: 12 }}
                  >
                    {TOBOGGAN_STATUSES.map(s => (
                      <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography fontSize={13} fontWeight={600} sx={{ flexShrink: 0 }}>
                  {toboggan.status}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Bottom bar */}
      {isEditing && (
        <Box sx={{
          borderTop: '1px solid', borderColor: 'divider',
          px: 1.5, py: 1.25,
          display: 'flex', gap: 1,
          position: 'sticky', bottom: 0,
          bgcolor: 'background.paper',
        }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isSaving}
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            size="small"
            sx={{ flex: 1 }}
          >
            {isSaving ? 'Saving…' : 'Save All'}
          </Button>
        </Box>
      )}
    </Card>
  );
};
